import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { Button, Box, Typography } from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckIcon from '@mui/icons-material/Check'
import { SnackbarHelperContext } from '../../contexts/SnackbarHelperContext'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import { firebase_functions } from '../../common/firebaseConfig'
import MultiSlideDialog, { SlideStep } from '../common/MultiSlideDialog'
import MetaTraderAccountForm, { MetaTraderAccount } from '../forms/MetaTraderAccountForm'

interface DisconnectedAccount {
  accountId: string
  login: string
  name?: string
  server: string
  platform: 'mt4' | 'mt5'
  deploymentState?: string
}

function ReconnectMetaApiAccountDialog({
  open,
  accounts,
  onClose,
  onRemindLater,
}: {
  open: boolean
  accounts: DisconnectedAccount[]
  onClose: () => void
  onRemindLater?: () => void
}) {
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)

  const [accountsSnapshot] = useState<DisconnectedAccount[]>(() => [...accounts])
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null)
  const [completedAccountIds, setCompletedAccountIds] = useState<string[]>([])
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // MultiSlideDialog only passes onNextStep to actionsComponents, not slideComponents.
  // This ref lets the slide-1 actionsComponent share its onNextStep so per-row buttons can advance.
  const advanceToFormRef = useRef<(() => void) | null>(null)

  const [reconnectFunc, reconnectLoading] = useHttpsCallable(
    firebase_functions,
    'reconnectMetaApiAccount'
  )

  const pendingAccounts = useMemo(
    () => accountsSnapshot.filter((a) => !completedAccountIds.includes(a.accountId)),
    [accountsSnapshot, completedAccountIds]
  )

  const currentAccount = useMemo(
    () => accountsSnapshot.find((a) => a.accountId === selectedAccountId) ?? null,
    [accountsSnapshot, selectedAccountId]
  )

  const handleAccountKeyChange = useCallback((key: string, value: any) => {
    if (key === 'password') setPassword(value)
  }, [])

  const handleClose = useCallback(() => {
    setErrorMessage(null)
    onClose()
  }, [onClose])

  const handleSelectAccount = useCallback((accountId: string) => {
    setSelectedAccountId(accountId)
    setPassword('')
    setShowPassword(false)
    setErrorMessage(null)
    advanceToFormRef.current?.()
  }, [])

  const handleBack = useCallback((onPreviousStep: () => void) => {
    setSelectedAccountId(null)
    setPassword('')
    setErrorMessage(null)
    onPreviousStep()
  }, [])

  const reconnect = useCallback(
    async (onPreviousStep: () => void, onNextStep: () => void) => {
      if (!selectedAccountId) return
      setErrorMessage(null)
      const remaining = pendingAccounts.length - 1

      try {
        const response: any = await reconnectFunc({
          oldAccountId: selectedAccountId,
          password,
        })

        if (!response?.data?.status || response.data.status >= 400) {
          const msg = response?.data?.message || 'Failed to reconnect account'
          setErrorMessage(msg)
          setSnackbarSettings({ severity: 'error', message: msg, autoHideDuration: 6000 })
          return
        }

        const label = currentAccount?.name || currentAccount?.login || ''
        setSnackbarSettings({
          severity: 'success',
          message: `${label} reconnected successfully!`,
          autoHideDuration: 4000,
        })

        setCompletedAccountIds((prev) => [...prev, selectedAccountId])
        setPassword('')
        setShowPassword(false)
        setErrorMessage(null)
        setSelectedAccountId(null)

        if (remaining > 0) {
          onPreviousStep()
        } else {
          onNextStep()
        }
      } catch {
        const msg = 'Failed to reconnect account'
        setErrorMessage(msg)
        setSnackbarSettings({ severity: 'error', message: msg, autoHideDuration: 6000 })
      }
    },
    [
      selectedAccountId,
      pendingAccounts.length,
      password,
      currentAccount,
      reconnectFunc,
      setSnackbarSettings,
    ]
  )

  const formAccount: MetaTraderAccount = useMemo(
    () => ({
      login: currentAccount?.login ?? '',
      password,
      server: currentAccount?.server ?? '',
      platform: currentAccount?.platform ?? 'mt4',
      nickname: currentAccount?.name ?? '',
    }),
    [currentAccount, password]
  )

  const steps: SlideStep[] = useMemo(
    () =>
      getSteps(
        advanceToFormRef,
        reconnect,
        handleBack,
        reconnectLoading ?? false,
        errorMessage,
        !password,
        onRemindLater
      ),
    [reconnect, handleBack, reconnectLoading, errorMessage, password, onRemindLater]
  )

  return (
    <MultiSlideDialog
      open={open}
      onClose={handleClose}
      title="Reconnect MetaTrader Account"
      steps={steps}
      slideProps={{
        pendingAccounts,
        onSelectAccount: handleSelectAccount,
        account: formAccount,
        onAccountKeyChange: handleAccountKeyChange,
        showPassword,
        setShowPassword,
        readOnlyFields: ['login', 'server', 'platform', 'nickname'],
        completedCount: completedAccountIds.length,
        totalAccounts: accountsSnapshot.length,
        currentAccountName: currentAccount?.name || currentAccount?.login || '',
      }}
    />
  )
}

// ── Slide components ─────────────────────────────────────────────────────────

function NoticeSlide({ pendingAccounts, onSelectAccount }: any) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      px={2}
      alignItems="center"
      textAlign="center"
    >
      <WarningAmberIcon sx={{ fontSize: '64px' }} color="warning" />
      <Typography variant="h4" fontWeight="bold">
        MetaTrader Accounts Disconnected
      </Typography>
      <Typography variant="body2" color="textSecondary">
        We detected {pendingAccounts.length} MetaTrader account
        {pendingAccounts.length > 1 ? 's' : ''} that {pendingAccounts.length > 1 ? 'have' : 'has'}{' '}
        been disconnected from your broker. Select an account below to re-enter its password and
        restore automated trading.
      </Typography>
      <Box display="flex" flexDirection="column" gap={1} width="100%">
        {pendingAccounts.map((account: DisconnectedAccount) => (
          <Box
            key={account.accountId}
            display="flex"
            alignItems="center"
            p={1.5}
            borderRadius={1}
            bgcolor="background.paper"
            border="1px solid"
            borderColor="divider"
          >
            <Box flex={1} textAlign="left">
              <Typography variant="body2" fontWeight="bold">
                {account.name || account.login}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {account.server} · {account.platform?.toUpperCase()}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={() => onSelectAccount(account.accountId)}
            >
              Reconnect
            </Button>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

function AccountFormSlide({
  account,
  onAccountKeyChange,
  showPassword,
  setShowPassword,
  readOnlyFields,
  currentAccountName,
}: any) {
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {currentAccountName && (
        <Typography variant="subtitle1" fontWeight="bold">
          {currentAccountName}
        </Typography>
      )}
      <Typography variant="body2" color="textSecondary">
        Enter your broker password to reconnect this account.
      </Typography>
      <MetaTraderAccountForm
        account={account}
        onAccountKeyChange={onAccountKeyChange}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        readOnlyFields={readOnlyFields}
      />
    </Box>
  )
}

function SuccessSlide({ completedCount, totalAccounts }: any) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      px={2}
      alignItems="center"
      textAlign="center"
    >
      <CheckIcon sx={{ fontSize: '64px' }} color="success" />
      <Typography variant="h4" fontWeight="bold">
        {completedCount === totalAccounts ? 'All Accounts Reconnected!' : 'Reconnection Complete'}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Reconnected {completedCount} of {totalAccounts} account{totalAccounts > 1 ? 's' : ''}. Your
        automated trading alerts have been updated.
      </Typography>
    </Box>
  )
}

// ── Step definitions ─────────────────────────────────────────────────────────

function getSteps(
  advanceToFormRef: React.MutableRefObject<(() => void) | null>,
  reconnect: (onPreviousStep: () => void, onNextStep: () => void) => Promise<void>,
  handleBack: (onPreviousStep: () => void) => void,
  isReconnecting: boolean,
  errorMessage: string | null,
  isPasswordEmpty: boolean,
  onRemindLater?: () => void
): SlideStep[] {
  return [
    {
      slideComponent: NoticeSlide,
      // Store onNextStep in ref so per-row Reconnect buttons (in the slide) can advance the dialog
      actionsComponent: ({ onNextStep }: any) => {
        advanceToFormRef.current = onNextStep
        if (!onRemindLater) return <Box />
        return (
          <Box display="flex" width="100%" height="100%" alignItems="center">
            <Button onClick={onRemindLater} variant="outlined" color="inherit" size="small">
              Remind Me Later
            </Button>
          </Box>
        )
      },
    },
    {
      slideComponent: AccountFormSlide,
      actionsComponent: ({ onNextStep, onPreviousStep }: any) => (
        <Box display="flex" gap={2} width="100%" height="100%" alignItems="center">
          {isReconnecting && (
            <Typography variant="body2" color="textSecondary" width="100%">
              This could take a few minutes to connect.
            </Typography>
          )}
          {errorMessage && (
            <Typography variant="body2" color="error" width="100%">
              {errorMessage}
            </Typography>
          )}
          {!errorMessage && isPasswordEmpty && (
            <Typography variant="body2" color="textSecondary" width="100%">
              * Please enter your password to continue.
            </Typography>
          )}
          <Box
            display="flex"
            gap={1}
            width="100%"
            height="100%"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Button
              onClick={() => handleBack(onPreviousStep)}
              variant="outlined"
              disabled={isReconnecting}
            >
              Back
            </Button>
            <Button
              onClick={() => reconnect(onPreviousStep, onNextStep)}
              variant="contained"
              color="success"
              disabled={isPasswordEmpty || isReconnecting}
            >
              {isReconnecting ? 'Connecting...' : 'Reconnect Account'}
            </Button>
          </Box>
        </Box>
      ),
    },
    {
      slideComponent: SuccessSlide,
      actionsComponent: ({ onClose }: any) => (
        <Box display="flex" justifyContent="center" width="100%" height="100%">
          <Button onClick={onClose} variant="contained" color="success">
            Done
          </Button>
        </Box>
      ),
    },
  ]
}

export default ReconnectMetaApiAccountDialog
export type { DisconnectedAccount }
